import { AnyAction } from 'redux';
import { Appearance, DraftConfiguration, DraftDesignConfiguration, ProductType } from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { isEmbroideryMode } from '@/utils/params';
import { getIsAdultDesignConfiguration } from '@/model/configuration/getIsAdultDesignConfiguration';
import { getPossiblePrintTypes } from '@/model/configuration/getPossiblePrintTypes';
import { isDraftDesignConfiguration } from '@/model/configuration/isDraftConfigurationType';
import { isRestrictedScaleBelowMinimumScale } from '@/model/configuration/isRestrictedScaleBelowMinimumScale';
import { isVectorDesign } from '@/model/design/isDesignType';
import { getAppearance } from '@/model/productType/getAppearance';
import { getIsAdultDesignsVerboten } from '@/model/productType/getIsAdultDesignsVerboten';
import { getIsSingleConfigurationAppearance } from '@/model/productType/getIsSingleConfigurationAppearance';
import { selectConfigurations } from '@/redux/draft/getters/selectConfigurations';
import { dismissModalAction } from '@/redux/modal/actions/dismissModalAction';
import { productTypeSwitchModalAction } from '@/redux/modal/actions/productTypeSwitchModalAction';
import { fetchProductTypeAction } from '@/redux/productTypes/actions/fetchProductTypeAction';
import { Thunk } from '@/redux/types';
import { switchProductTypeAction } from '@/components/draft/draftState/actions/switchProductTypeAction';

const getUnprintableConfigurationsForProductTypeSwitch = (
  configurations: DraftConfiguration[],
  productType: ProductType,
  appearance: Appearance
): DraftConfiguration[] => {
  const getIsPrintable = (configuration: DraftConfiguration) =>
    productType.printAreas.some(
      (printArea) =>
        !printArea.deprecated &&
        getPossiblePrintTypes(configuration, printArea, appearance).length !== 0
    );

  const canBeScaledBelowRestrictedScale = (configuration: DraftDesignConfiguration) =>
    !productType.printAreas.some(
      (printArea) =>
        !printArea.deprecated &&
        isRestrictedScaleBelowMinimumScale(configuration, productType, printArea.id)
    );

  const isSingleConfiguration = getIsSingleConfigurationAppearance(appearance);
  let hasFoundSingleConfiguration = false;

  return configurations.filter((configuration) => {
    const isPrintable = getIsPrintable(configuration);
    if (!isPrintable) {
      return true;
    }

    if (
      isEmbroideryMode() &&
      isDraftDesignConfiguration(configuration) &&
      isVectorDesign(configuration.design) &&
      !canBeScaledBelowRestrictedScale(configuration)
    ) {
      return true;
    }

    if (isSingleConfiguration) {
      if (hasFoundSingleConfiguration) {
        return true;
      }

      hasFoundSingleConfiguration = true;
    }

    return false;
  });
};

export const productTypeSwitchAction =
  (
    productTypeId: string,
    appearanceId: string,
    sizeId: string | undefined,
    onSwitched: () => void
  ): Thunk<void, AnyAction> =>
  async (dispatch, getState) => {
    const productType = await dispatch(fetchProductTypeAction(productTypeId));
    const appearance = assertDefined(getAppearance(productType, appearanceId));

    const state = getState();
    const draftDispatch = state.draftDispatch;

    const dispatchSwitchProductType = () => {
      draftDispatch(switchProductTypeAction(productType, appearance, sizeId));
      dispatch(dismissModalAction);
      onSwitched();
    };

    const configurations = selectConfigurations(state);
    const adultContentConfigs = configurations.filter((configuration) =>
      getIsAdultDesignConfiguration(configuration)
    );
    if (adultContentConfigs.length > 0 && getIsAdultDesignsVerboten(productType)) {
      dispatch(
        productTypeSwitchModalAction(
          adultContentConfigs.map(({ cid }) => cid),
          dispatchSwitchProductType,
          'adultContent'
        )
      );
      return;
    }

    const unprintableConfigs = getUnprintableConfigurationsForProductTypeSwitch(
      configurations,
      productType,
      appearance
    );
    if (unprintableConfigs.length > 0) {
      dispatch(
        productTypeSwitchModalAction(
          unprintableConfigs.map(({ cid }) => cid),
          dispatchSwitchProductType,
          'printability'
        )
      );
      return;
    }

    dispatchSwitchProductType();
  };
